import ForkRight from '@mui/icons-material/ForkRight';
import NoteAdd from '@mui/icons-material/NoteAdd';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    IconButton,
    Paper,
    Tooltip,
    Typography
} from '@mui/material';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import Dominios from '../util/Dominios';
import ReactComponent from './ReactComponent';
import ModalConfirmacao from './sessao/ModalConfirmacao';

class IconeDivergencia extends ReactComponent {
    abrirDialog = false;
    divergencias = [];
    abrirConfirmacaoAdicionarRascunho = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            abrirDialog: observable,
            divergencias: observable,
            abrirConfirmacaoAdicionarRascunho: observable,
            handleCloseDialog: action,
            existeVotoDivergente: computed,
            votosComDivergencia: computed,
            tooltip: computed,
            handleClickIcone: action,
            handleClickAdicionarRascunho: action,
            fecharConfirmacaoTextoDivergencia: action
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    handleCloseDialog = () => {
        this.abrirDialog = false;
    };

    get existeVotoDivergente() {
        return (
            this.registro.votos &&
            this.registro.votos.findIndex((voto) => voto.voto?.codigo === Dominios.CODIGO_VOTO_DIVERGENTE) !== -1
        );
    }

    get votosComDivergencia() {
        if (!this.registro.votos) {
            return [];
        }
        return this.registro.votos.reduce((acumulador, voto) => {
            if (
                voto.voto?.codigo === Dominios.CODIGO_VOTO_DIVERGENTE &&
                (voto.situacaoVoto === 'CONFIRMADO' || voto.situacaoVoto === 'PRE_VOTADO')
            ) {
                acumulador.push({
                    sigla: voto.siglaDoMagistrado,
                    situacaoVoto: voto.situacaoVoto
                });
            }
            return acumulador;
        }, []);
    }

    getDescricaoSituacaoVoto(situacaoVoto) {
        if (situacaoVoto === 'CONFIRMADO') {
            return 'Confirmado';
        }
        if (situacaoVoto === 'PRE_VOTADO') {
            return 'Pré-votado';
        }
    }

    get tooltip() {
        return (
            <div style={{ margin: '5px' }}>
                <div>Votação com divergência</div>
                <ul style={{ listStyleType: 'circle' }}>
                    {this.votosComDivergencia.map((voto) => (
                        <li key={this.nextKey}>
                            {voto.sigla + ' - ' + this.getDescricaoSituacaoVoto(voto.situacaoVoto)}
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    handleClickIcone = async () => {
        this.abrirDialog = true;
        this.divergencias = [];
        let votosConfirmados = this.votosComDivergencia.filter((voto) => voto.situacaoVoto === 'CONFIRMADO');
        let promises = votosConfirmados.map((voto) =>
            this.processoService.recuperarObservacaoVoto(
                this.registro.sessaoId,
                this.registro.processo.numeroCompleto,
                Dominios.TIPO_OBSERVACAO_VOTO_DIVERGENCIA,
                voto.sigla
            )
        );
        let respostas = await Promise.all(promises);
        let observacoesVoto = this.consolidarRespostas(respostas);
        for (let index in observacoesVoto) {
            let observacaoVoto = observacoesVoto[index];
            let sigla = observacaoVoto.magistrado.sigla;
            let conteudo = observacaoVoto.conteudo;
            if (!observacaoVoto.tipoConteudo) {
                conteudo = (
                    await this.processoService.recuperarObservacaoVotoPeloId(
                        this.registro.sessaoId,
                        this.registro.processo.numeroCompleto,
                        Dominios.TIPO_OBSERVACAO_VOTO_DIVERGENCIA,
                        sigla,
                        observacaoVoto.id
                    )
                ).conteudo;
            } else if (observacaoVoto.tipoConteudo === 'HTML') {
                conteudo = await this.processoService.recuperarConteudoObservacaoVotoPeloId(
                    this.registro.sessaoId,
                    this.registro.processo.numeroCompleto,
                    Dominios.TIPO_OBSERVACAO_VOTO_DIVERGENCIA,
                    sigla,
                    observacaoVoto.id
                );
            }
            runInAction(() => this.divergencias.push({ sigla: sigla, conteudo: conteudo }));
        }
        if (this.divergencias.length === 0) {
            runInAction(() => (this.abrirDialog = false));
            this.mensagemStore.abrirMensagemAlerta(
                'Não há observações de divergências de votos confirmados a serem exibidos'
            );
        }
    };

    consolidarRespostas(respostas) {
        let observacoesVoto = [];
        respostas.forEach((resposta) => {
            observacoesVoto = observacoesVoto.concat(resposta.filter((resp) => typeof resp === 'object'));
        });
        return observacoesVoto;
    }

    handleClickAdicionarRascunho = async (texto) => {
        texto = this.limparHtml(texto);
        let rascunho = await this.certidaoService.recuperarRascunhoCertidao(this.registro);
        if (rascunho.indexOf(texto) === -1 || this.abrirConfirmacaoAdicionarRascunho) {
            rascunho += '\n\n' + texto;
            await this.certidaoService.salvarRascunhoCertidao(this.registro, rascunho);
            runInAction(() => (this.registro.rascunhoCertidao = rascunho));
            this.mensagemStore.abrirMensagemSucesso('Texto da divergência adicionado ao rascunho da certidão');
            this.abrirConfirmacaoAdicionarRascunho = false;
        } else {
            this.abrirConfirmacaoAdicionarRascunho = true;
        }
    };

    limparHtml(html) {
        let tmp = document.createElement('DIV');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    }

    fecharConfirmacaoTextoDivergencia = () => {
        this.abrirConfirmacaoAdicionarRascunho = false;
    };

    renderConfirmacao(divergencia) {
        return (
            <ModalConfirmacao
                titulo={'Adicionar texto da divergência no rascunho da certidão'}
                open={this.abrirConfirmacaoAdicionarRascunho}
                callbackCancelar={this.fecharConfirmacaoTextoDivergencia}
                callbackConfirmar={() => this.handleClickAdicionarRascunho(divergencia)}
            >
                <Typography display={'inline'}>
                    O texto da divergência foi encontrado no rascunho da certidão. Deseja adicionar?
                </Typography>
            </ModalConfirmacao>
        );
    }

    render() {
        if (this.existeVotoDivergente) {
            return (
                <div style={{ margin: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Tooltip
                        title={this.tooltip}
                        placement="top"
                    >
                        <ForkRight
                            style={{
                                cursor: 'pointer',
                                fillOpacity: '0.7',
                                fill: 'darkorange',
                                transform: 'rotate(90deg)'
                            }}
                            onClick={this.handleClickIcone}
                        />
                    </Tooltip>
                    {this.abrirDialog && (
                        <Dialog
                            onClose={this.handleCloseDialog}
                            open={this.abrirDialog}
                            maxWidth={false}
                        >
                            <DialogTitle
                                id="simple-dialog-title"
                                style={{ paddingBottom: '0px' }}
                            >
                                {'Divergências do ' + this.registro.processo.numeroCompleto}
                            </DialogTitle>
                            {this.divergencias.length > 0 ? (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        margin: '0px 10px',
                                        overflowX: 'auto'
                                    }}
                                >
                                    {this.divergencias.map((divergencia) => {
                                        return (
                                            <Paper
                                                key={this.nextKey}
                                                elevation={10}
                                                style={{ display: 'flex', flexDirection: 'column', margin: '10px' }}
                                            >
                                                <div style={{ margin: '10px' }}>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        <h4 style={{ margin: '10px 0px' }}>{divergencia.sigla}</h4>
                                                        <Tooltip
                                                            title="Adicionar o texto da divergência ao rascunho da certidão"
                                                            placement="top"
                                                        >
                                                            <IconButton
                                                                size="large"
                                                                onClick={() =>
                                                                    this.handleClickAdicionarRascunho(
                                                                        divergencia.conteudo
                                                                    )
                                                                }
                                                            >
                                                                <NoteAdd style={{ color: 'black' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {this.renderConfirmacao(divergencia.conteudo)}
                                                    </div>
                                                    <div
                                                        dangerouslySetInnerHTML={{ __html: divergencia.conteudo }}
                                                        style={{
                                                            overflowX: 'auto',
                                                            minWidth: '300px',
                                                            maxWidth: '640px',
                                                            minHeight: '60px',
                                                            maxHeight: '200px'
                                                        }}
                                                    />
                                                </div>
                                            </Paper>
                                        );
                                    })}
                                </div>
                            ) : (
                                this.appStore.requisicoesPendentes && (
                                    <CircularProgress
                                        style={{ margin: '15px' }}
                                        size={30}
                                        disableShrink={true}
                                    />
                                )
                            )}
                            <DialogActions>
                                <Button
                                    onClick={this.handleCloseDialog}
                                    color="primary"
                                >
                                    Ok
                                </Button>
                            </DialogActions>
                        </Dialog>
                    )}
                </div>
            );
        }
        return null;
    }
}

export default inject('stores')(observer(IconeDivergencia));
